/**
 * RemoteControlService — the TV<->Remote link, rebuilt over the room-do
 * WebSocket. This is the Cloudflare analogue of spot-client's
 * `BaseRemoteControlService` (+ `remoteControlServer` / `remoteControlClient`):
 * the SAME responsibilities — connect to the room, relay commands, broadcast and
 * receive presence + roster, correlate command acks within `ACK_TIMEOUT_MS` — but
 * the transport underneath is {@link DOConnection} (ControlEnvelope over WS), NOT
 * an XMPP MUC. The command protocol and presence shape are the frozen
 * @spaces/contracts ones, so the UI carries over unchanged.
 *
 * One class, two roles (mirrors spot's one-bundle-two-roles split):
 *   - TV     — publishes presence, receives commands, sends acks.
 *   - Remote — sends commands (awaiting a correlated ack), receives presence.
 */

import {
    ACK_TIMEOUT_MS,
    type AckEnvelope,
    type ClientType,
    type CommandEnvelope,
    type ControlEnvelope,
    type ErrorEnvelope,
    type MessageEnvelope,
    type RoomOccupant,
    type SpotTvPresence
} from '@spaces/contracts';
import type { ClientCommandName } from '../commands.js';
import { Emitter, log } from '../log.js';
import { DOConnection } from './DOConnection.js';

/** Events any consumer (TV or Remote UI) can subscribe to. */
export interface RcsEvents {
    /** TV presence broadcast (Remote consumes this to drive its UI). */
    presence: SpotTvPresence;
    /** Room occupant roster (both roles). */
    roster: RoomOccupant[];
    /** A command relayed to this endpoint (TV consumes this to drive the meeting). */
    command: CommandEnvelope;
    /** A relayed pass-through message. */
    message: MessageEnvelope;
    /** A server error frame (room-level — NOT a per-command failure). */
    error: ErrorEnvelope;
    /** A drop occurred and the transport is auto-reconnecting (the link is NOT lost yet). */
    reconnecting: { attempt: number };
    /** A reconnect succeeded — the link is live again. */
    reconnected: void;
    /** The socket is gone for good (server-intentional close, or retries exhausted). */
    disconnect: { code: number; reason: string };
}

export interface RcsOptions {
    roomDoUrl: string;
    roomId: string;
    endpointId: string;
    accessToken: string;
    clientType: ClientType;
    joinCode?: string;
    /** Reconnect budget after a drop (Remote defaults to a bounded retry; TV passes Infinity). */
    maxReconnectAttempts?: number;
    /** Optional fresh-token minter used before each reconnect (the TV supplies it). */
    refreshAccessToken?: () => Promise<string>;
}

/** A pending command awaiting its ack. */
interface PendingAck {
    resolve: (ack: AckEnvelope) => void;
    reject: (err: Error) => void;
    timer: ReturnType<typeof setTimeout>;
}

export class RemoteControlService {
    private readonly options: RcsOptions;
    private readonly emitter = new Emitter<RcsEvents>();
    private readonly pending = new Map<string, PendingAck>();
    private connection: DOConnection | null = null;

    constructor(options: RcsOptions) {
        this.options = options;
    }

    /** Subscribe to a service event. Returns an unsubscribe fn. */
    on<K extends keyof RcsEvents>(event: K, handler: (payload: RcsEvents[K]) => void): () => void {
        return this.emitter.on(event, handler);
    }

    /** Open the room-do socket and announce this endpoint (sends `hello`). */
    async connect(): Promise<void> {
        this.connection = new DOConnection({
            roomDoUrl: this.options.roomDoUrl,
            roomId: this.options.roomId,
            endpointId: this.options.endpointId,
            accessToken: this.options.accessToken,
            clientType: this.options.clientType,
            joinCode: this.options.joinCode,
            maxReconnectAttempts: this.options.maxReconnectAttempts,
            refreshAccessToken: this.options.refreshAccessToken,
            onFrame: frame => this.handleFrame(frame),
            onReconnecting: attempt => this.emitter.emit('reconnecting', { attempt }),
            onReconnected: () => this.emitter.emit('reconnected', undefined),
            onClose: (code, reason) => {
                // Terminal: the link is truly gone. Reject any in-flight commands so
                // their callers don't hang on the ack timeout.
                this.failAllPending('disconnected');
                this.emitter.emit('disconnect', { code, reason });
            }
        });

        await this.connection.open();
    }

    /** Tear down the socket and reject any outstanding command acks. */
    disconnect(): void {
        this.failAllPending('disconnected');
        this.connection?.close();
        this.connection = null;
        this.emitter.clear();
    }

    /** Reject every in-flight command (on a terminal drop or a local teardown). */
    private failAllPending(reason: string): void {
        for (const [, pending] of this.pending) {
            clearTimeout(pending.timer);
            pending.reject(new Error(reason));
        }
        this.pending.clear();
    }

    // ---- Remote role ---------------------------------------------------------

    /**
     * Send a command to the TV and resolve with its correlated ack (or reject on
     * `ACK_TIMEOUT_MS`). Mirrors the legacy IQ id + 5s timeout exactly.
     */
    sendCommand(command: ClientCommandName, payload?: unknown): Promise<AckEnvelope> {
        const id = crypto.randomUUID();
        // The wire command string is widened to allow the pending `setRecording`
        // addition; the room-do relays it opaquely. Frozen CommandEnvelope types
        // `command` as CommandName, so cast at this single boundary.
        const frame = { kind: 'command', id, command, payload } as unknown as CommandEnvelope;

        return new Promise<AckEnvelope>((resolve, reject) => {
            const timer = setTimeout(() => {
                this.pending.delete(id);
                reject(new Error(`command '${String(command)}' timed out after ${ACK_TIMEOUT_MS}ms`));
            }, ACK_TIMEOUT_MS);

            this.pending.set(id, { resolve, reject, timer });
            log.log('RCS sendCommand', { command, id });
            this.send(frame);
        });
    }

    // ---- TV role -------------------------------------------------------------

    /** TV: broadcast presence to all Remotes (cached by room-do for join-time replay). */
    publishPresence(status: SpotTvPresence): void {
        this.send({ kind: 'presence', status });
    }

    /** TV: acknowledge a command, correlated by its id. */
    sendAck(id: string, ok: boolean, error?: string): void {
        this.send({ kind: 'ack', id, ok, ...(error ? { error } : {}) });
    }

    // ---- Shared --------------------------------------------------------------

    /** Send a relay (pass-through) message to the other role or a specific endpoint. */
    sendMessage(message: MessageEnvelope): void {
        this.send(message);
    }

    private send(frame: ControlEnvelope): void {
        this.connection?.send(frame);
    }

    private handleFrame(frame: ControlEnvelope): void {
        switch (frame.kind) {
        case 'presence':
            this.emitter.emit('presence', frame.status);
            break;
        case 'roster':
            this.emitter.emit('roster', frame.occupants);
            break;
        case 'command':
            this.emitter.emit('command', frame);
            break;
        case 'ack':
            this.resolveAck(frame);
            break;
        case 'message':
            this.emitter.emit('message', frame);
            break;
        case 'error':
            log.warn('RCS error frame', frame);

            // A correlated error answers a specific command (e.g. the relay failed
            // because no TV is connected): fail THAT command now instead of letting
            // it hang to ACK_TIMEOUT_MS, and don't raise a room-level error for it.
            if (frame.id) {
                const pending = this.pending.get(frame.id);

                if (pending) {
                    clearTimeout(pending.timer);
                    this.pending.delete(frame.id);
                    pending.reject(new Error(frame.message || frame.code));
                    break;
                }
            }

            this.emitter.emit('error', frame);
            break;
        case 'hello':
            break; // server never sends hello to us
        }
    }

    private resolveAck(ack: AckEnvelope): void {
        const pending = this.pending.get(ack.id);

        if (!pending) {
            return; // unknown id (or a broadcast ack we didn't originate) — ignore
        }

        clearTimeout(pending.timer);
        this.pending.delete(ack.id);
        pending.resolve(ack);
    }
}
